import { useState } from "react";
import { usePaymentMethods } from "../../../hooks/paymentMethod/usePaymentMethods";
import { LoadingSpinner } from "../../common/loadingSpinner";
import { ViewButton } from "../../common/ui/button/icon/viewButton";
import { PaymentMethodDetailModal } from "../../common/modal/paymentMethodDetailModal";
import type { PaymentMethodResponse } from "../../../hooks/requests/paymentMethod";
import { GATEWAY_PAYMENT_METHODS } from "../../../constants/gatewayPaymentMethods";

const TICKET_TYPES = [
  { label: "Plan", key: "plan" as const },
  { label: "Alimentación", key: "food" as const },
  { label: "Mercadería", key: "merchandise" as const },
];

const TicketBadge = ({
  label,
  requires,
}: {
  label: string;
  requires: boolean;
}) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-size-2xs font-medium border ${
      requires
        ? "bg-global-success text-global-success border-(--color-success-border)"
        : "bg-global-overlay-secondary text-global-muted border-global-secondary"
    }`}
  >
    {requires ? (
      <svg
        className="w-3 h-3"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2.5}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    ) : (
      <svg
        className="w-3 h-3"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
      </svg>
    )}
    {label}
  </span>
);

const PaymentMethodCard = ({
  pm,
  onView,
}: {
  pm: PaymentMethodResponse;
  onView: () => void;
}) => {
  const gatewayLogo = GATEWAY_PAYMENT_METHODS.find(
    (gw) => gw.key === pm.key,
  )?.logo;
  return (
    <div className="card-section flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {gatewayLogo ? (
            <div className="h-8 w-8 shrink-0 rounded-lg bg-white border border-global-secondary flex items-center justify-center p-1">
              <img
                src={gatewayLogo}
                alt={pm.name}
                className="h-full w-full object-contain"
              />
            </div>
          ) : (
            <div className="w-8 h-8 rounded-lg bg-global-overlay-secondary border border-global-secondary flex items-center justify-center shrink-0">
              <svg
                className="w-4 h-4 text-global-secondary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
            </div>
          )}
          <span className="text-size-sm font-semibold text-global-primary truncate">
            {pm.name}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-size-2xs font-medium border ${
              pm.active
                ? "bg-global-success text-global-success border-(--color-success-border)"
                : "bg-global-error text-global-error border-(--color-error-border)"
            }`}
          >
            {pm.active ? "Activo" : "Inactivo"}
          </span>
          <ViewButton onClick={onView} />
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-global-secondary">
        <span className="text-size-2xs text-global-muted uppercase tracking-wide mr-1">
          Ticket:
        </span>
        {TICKET_TYPES.map(({ label, key }) => (
          <TicketBadge
            key={key}
            label={label}
            requires={pm.ticketConfig?.[key]?.requires ?? false}
          />
        ))}
      </div>
    </div>
  );
};

export const GymPaymentMethods = () => {
  const [viewingPaymentMethod, setViewingPaymentMethod] =
    useState<PaymentMethodResponse | null>(null);
  const { data: paymentMethods, isLoading } = usePaymentMethods();
  const manualMethods = paymentMethods?.filter((pm) => pm.type !== "gateway");

  return (
    <>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : !manualMethods || manualMethods.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
          <div className="w-10 h-10 rounded-full bg-global-overlay-secondary flex items-center justify-center">
            <svg
              className="w-5 h-5 text-global-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          </div>
          <p className="text-size-sm text-global-muted">
            No hay métodos de cobro configurados
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {manualMethods.map((pm) => (
            <PaymentMethodCard
              key={pm.publicId}
              pm={pm}
              onView={() => setViewingPaymentMethod(pm)}
            />
          ))}
        </div>
      )}

      {viewingPaymentMethod && (
        <PaymentMethodDetailModal
          paymentMethod={viewingPaymentMethod}
          isOpen={!!viewingPaymentMethod}
          onClose={() => setViewingPaymentMethod(null)}
        />
      )}
    </>
  );
};
